<template>
  <div>
    <div v-if="isLoad">
      <div v-if="!detail" class="columns is-multiline">
        <selecteur-action
          @handleAction="handleAction"
          :options="actions"
          :placeholder='$t("general.placeHolder.groupedAction")'
          :elementsSelectionnes="getAllSelected.length > 0"
          titleButton="general.button.apply"
          class="column is-narrow mgb-0">
        </selecteur-action>
        <div class="column has-text-right">
          <a class="button is-addOrEdit" @click="addLigne()">
            {{ $t('produitSalon.cgv.actions.add')}}
          </a>
        </div>
      </div>
      <div v-else class="columns is-justify-content-end is-align-items-end">
        <div class="column" v-if="produit.regroupementProduit && showMsgRegroupementProduit">
          <msg-info-produit :regroupementProduit="produit.regroupementProduit"/>
        </div>
        <div class="column is-narrow">
          <exportExcelButton
            :perm="permissions.exporter"
            :query="exportExcelQuery"/>
        </div>
      </div>
    </div>
    <v-client-table
      :data="produit.cgvs"
      :columns="columns"
      :options="options"
      class="displayNoneVuePagination mgt-20px">
      <template #selected="{ row, index }">
        <qd-checkbox
          v-model="row.selected"
          @input="updateValue(row, 'selected', index)"
          class="mgt-5px">
        </qd-checkbox>
      </template>
      <template #libelleRemiseId="{ row, index }">
        <b-select
          v-if="!detail"
          v-model="row.libelleRemiseId"
          @input="updateValue(row, 'libelleRemiseId', index)"
          expanded>
          <option :value="null"></option>
          <option
            v-for="item in remises"
            :key="item.id"
            :value="item.id">
            {{ item.libelle }}
          </option>
        </b-select>
        <span v-else> {{getRemise(row.libelleRemiseId)}} </span>
      </template>
      <template #remise="{ row, index }">
        <b-input
          v-if="!detail"
          v-model.number="row.remise"
          :use-html5-validation="false"
          @input="updateValue(row, 'remise', index)"
          type="number"
          min="-100" max="100"
          step="0.01">
        </b-input>
        <span v-else> {{row.remise | formatPercent}} </span>
      </template>
      <template #type="{ row, index }">
        <b-select
          v-if="!detail"
          v-model="row.type"
          @input="updateValue(row, 'type', index)"
          expanded>
          <option
            v-for="item in constantes.LISTE_TYPE_REMISE.filter(el => el.show)"
            :key="item.code"
            :value="item.code">
            {{ $t(item.libelle) }}
          </option>
        </b-select>
        <span v-else> {{getCgvActionType(row.type)}} </span>
      </template>
      <template #delete="{ row }">
        <a @click="deleteLigne(row)">
          <b-icon icon="delete-forever" type="is-danger"/>
        </a>
      </template>
    </v-client-table>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import genereCgv from '@/services/presse/salon/genereCgv';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import selecteurAction from '@/components/general/selecteurAction';
import MsgInfoProduit from '@/components/presse/regroupementProduit/informations/MsgInfoProduit';
import messageErreur from '@/services/general/messageErreur';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { LISTE_TYPE_REMISE } from '@/configuration/devisConf';
import { EXPORT_EXCEL_CGV } from '@/configuration/presse/produitPresse/produitPresseConf';
import {
  ACTION_SUPPRESSION,
} from '@/configuration/presse/produitSalon/produitSalonConfiguration';
import { columnsSplice } from '@/services/general/columns';
import exportExcelButton from '@/components/general/button/exportExcelButton';

const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'CatalogueProduitGeneralPresseEditCGV',
  props: {
    produit: {
      type: Object,
      required: true,
    },
    detail: {
      type: Boolean,
    },
    showMsgRegroupementProduit: {
      type: Boolean,
    },
    permissions: {
      type: Object,
      required: true,
    },
  },
  components: {
    selecteurAction,
    MsgInfoProduit,
    exportExcelButton,
  },
  data() {
    return {
      isLoad: false,
      constantes: {
        LISTE_TYPE_REMISE,
      },
      actions: [
        { action: ACTION_SUPPRESSION, translation: 'produitSalon.cgv.actions.supprimer' },
      ],
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    allIsSelected() {
      return this.getAllSelected.length === this.produit.cgvs.length;
    },
    iconSelectTotal() {
      if (this.allIsSelected) {
        return 'checkbox-multiple-marked-outline';
      }
      return 'checkbox-multiple-blank-outline';
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          libelleRemiseId: this.$t('produitSalon.cgv.information.libelleRemise'),
          remise: this.$t('produitSalon.cgv.information.remise'),
          type: this.$t('produitSalon.cgv.information.type'),
          delete: '',
          selected: (h) => {
            return h(
              'a', {
                attrs: {
                  class: 'pointer',
                },
                on: {
                  click: () => {
                    const allSelected = this.allIsSelected;
                    this.produit.cgvs.forEach(c => {
                      c.selected = !allSelected;
                    });
                  },
                },
              },
              [
                h('b-icon', {
                  attrs: {
                    icon: this.iconSelectTotal,
                  },
                }),
              ],
            );
          },
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        columnsClasses: {
          delete: 'colonneSelected',
          selected: 'colonneSelected',
        },
        editableColumns: ['selected', 'libelleRemiseId', 'remise', 'type'],
        uniqueKey: 'idTempo',
        sortable: this.detail ? ['libelleRemiseId', 'remise', 'type'] : [],
      };
    },
    columns() {
      const columns = ['selected', 'libelleRemiseId', 'remise', 'type', 'delete'];
      if (this.detail) {
        columnsSplice(columns, 'selected');
        columnsSplice(columns, 'delete');
      }
      return columns;
    },
    getAllSelected() {
      return this.produit.cgvs.filter(c => c.selected);
    },
    remises() {
      return this.parametre(PARAMETRES_KEYS.remise);
    },
    exportExcelQuery() {
      return {
        typeDonnee: EXPORT_EXCEL_CGV,
        typeExport: null,
        filtres: {
          query: { produitId: this.produit.id },
        },
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    updateValue(row, key, index) {
      if (!index) return;
      this.$set(this.produit.cgvs[index - 1], key, row[key]);
    },
    getRemise(id) {
      const find = this.remises.find(u => u.id === id);
      if (find) return find.libelle;
      return '';
    },
    getCgvActionType(value) {
      const find = LISTE_TYPE_REMISE.find(c => c.code === value);
      if (find) return this.$t(find.libelle);
      return '';
    },
    addLigne() {
      this.produit.cgvs.unshift(genereCgv());
    },
    deleteLigne(row) {
      const index = this.produit.cgvs
        .findIndex(cgv => cgv.id === row.id || cgv.idTempo === row.idTempo);
      this.produit.cgvs.splice(index, 1);
    },
    handleAction(type) {
      switch (type) {
        case ACTION_SUPPRESSION:
          if (!this.allIsSelected) {
            this.getAllSelected.forEach(el => {
              this.deleteLigne(el);
            });
          } else this.produit.cgvs = [];
          break;
        default:
          break;
      }
    },
  },
  async mounted() {
    this.isLoad = false;
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([
        PARAMETRES_KEYS.remise,
      ]);
      this.isLoad = true;
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
    this.produit.cgvs.forEach(c => { c.idTempo = c.id; });
  },
};
</script>
